// cspell:disable

import { components } from "@redotech/returnbear-openapi";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { WEIGHT_UNITS } from "./shared/common-enums";
import { CURRENCY_CODES } from "./shared/currency-codes";
import { caseInsensitiveEnum } from "./shared/utils";

export const RETURN_EXCEPTION_TYPES = [
  "DAMAGED_ITEM",
  "ITEM_NOT_RECEIVED",
  "UNDECLARED_ITEM",
] as const;

export const RETURN_EXCEPTION_RESOLUTIONS = ["PENDING", "RESOLVED"] as const;

export const RESOLUTION_INSTRUCTIONS = [
  "DISPOSE",
  "PROCESS",
  "RETURN_TO_CUSTOMER",
] as const;

export const OUTBOUND_TRACKING_CARRIERS = [
  "AUSTRALIA_POST_MYPOST_BUSINESS",
  "CANADA_POST",
  "CANPAR",
  "DHL_ECOMMERCE",
  "DHL_EXPRESS",
  "DHL_EXPRESS_CA",
  "FEDEX",
  "PUROLATOR_CA",
  "ROYAL_MAIL",
  "STAMPS_COM",
  "UPS",
  "USPS",
  "OTHER",
] as const;

export const ITEM_CONDITIONS = ["DAMAGED", "LIKE_NEW"] as const;

export type CreateReturnBearCaseRequest =
  components["schemas"]["CreateReturnCaseRequest"];

export type CreateReturnBearCaseRequestItem =
  components["schemas"]["ReturnCaseItem"];

export type CreateReturnBearCaseResponse =
  components["schemas"]["CreateReturnCaseResponse"];

export type ShowReturnBearCaseResponse =
  components["schemas"]["ShowReturnCaseResponse"];

export type ListAllReturnBearCaseMethodsResponse =
  components["schemas"]["ListReturnMethodsResponse"];

export type SelectReturnBearCaseMethodResponse =
  components["schemas"]["SelectReturnMethodResponse"];

export type SelectCourierReturnMethodResponse =
  components["schemas"]["SelectCourierReturnMethodResponse"];

export type SelectReturnPointReturnMethodResponse =
  components["schemas"]["ReturnPointReturnMethodResponse"];

export type ReturnPointLocationDistance = components["schemas"]["Distance"];

export type ReturnCaseItemOrderLineDetails =
  components["schemas"]["OrderLineDetails"];

export type ReturnBearCaseCustomerRequest =
  components["schemas"]["CustomerRequest"];

export type ReturnBearCaseExternalReferences =
  components["schemas"]["ExternalReferences"];

export type ResolveReturnExceptionRequest =
  components["schemas"]["ResolveReturnExceptionRequest"];

export type ReturnExceptionPendingResolutionResponse =
  components["schemas"]["ReturnExceptionPendingResolutionResponse"];

export type ReturnException = components["schemas"]["ReturnException"];

export type DamagedItemReturnException =
  components["schemas"]["DamagedItemReturnException"];

export type ItemNotReceivedReturnException =
  components["schemas"]["ItemNotReceivedReturnException"];

export type UndeclaredItemReturnException =
  components["schemas"]["UndeclaredItemReturnException"];

export type ReturnCaseTrackingEvent =
  components["schemas"]["ReturnCaseTrackingEvent"];

export type ReturnCaseTrackingEventLocation = components["schemas"]["Location"];

export type ResolutionInstruction =
  components["schemas"]["ResolutionInstructionEnum"];

export type DamagedItemResolution =
  components["schemas"]["DamagedItemResolution"];

export type ItemNotReceivedResolution =
  components["schemas"]["ItemNotReceivedResolution"];

export type UndeclaredItemResolution =
  components["schemas"]["UndeclaredItemResolution"];

export type ItemDetailsForProcessing =
  components["schemas"]["ItemDetailsForProcessing"];

export type ReturnExceptionType = (typeof RETURN_EXCEPTION_TYPES)[number];

export type ReturnExceptionResolution =
  (typeof RETURN_EXCEPTION_RESOLUTIONS)[number];

export type ItemCondition = (typeof ITEM_CONDITIONS)[number];

export const DamagedItemReturnDetailsExceptionSchema = z.object({
  return_item_id: z.string(),
  condition: z.literal("DAMAGED"),
  sku: z.string(),
  photos: z.array(z.string()),
  returnbear_note: z.string().optional(),
}) satisfies z.ZodType<DamagedItemReturnException>;

export const ItemNotReceivedReturnDetailsExceptionSchema = z.object({
  return_item_id: z.string(),
  returnbear_note: z.string().optional(),
}) satisfies z.ZodType<ItemNotReceivedReturnException>;

export const UndeclaredItemReturnDetailsExceptionSchema = z.object({
  undeclared_item_id: z.string(),
  condition: createEnum(ITEM_CONDITIONS),
  photos: z.array(z.string()),
  returnbear_note: z.string().optional(),
}) satisfies z.ZodType<UndeclaredItemReturnException>;

export const ReturnExceptionSchema = z.object({
  id: z.string().optional(),
  type: createEnum(RETURN_EXCEPTION_TYPES).optional(),
  resolution: createEnum(RETURN_EXCEPTION_RESOLUTIONS).optional(),
  data: z
    .union([
      DamagedItemReturnDetailsExceptionSchema,
      ItemNotReceivedReturnDetailsExceptionSchema,
      UndeclaredItemReturnDetailsExceptionSchema,
    ])
    .optional(),
}) satisfies z.ZodType<ReturnException>;

export const ItemDetailsForProcessingSchema = z.object({
  country_code_of_origin: z.string(),
  harmonized_system_code: z.string(),
  manufacturer_details: z.object({
    name: z.string(),
    address: z.object({
      address1: z.string(),
      address2: z.string().optional().nullable(),
      city: z.string(),
      country_code: z.string(),
      postal_code: z.string(),
      province: z.string(),
      first_name: z.string().optional(),
      last_name: z.string().optional(),
    }),
  }),
  order_identifier: z.object({
    type: createEnum(["ORDER_NUMBER", "ORDER_ID"]),
    value: z.string(),
  }),
  outbound_tracking: z.object({
    carrier: createEnum(OUTBOUND_TRACKING_CARRIERS),
    tracking_number: z.string(),
  }),
  sale_price: z.object({
    amount: z.number(),
    currency: caseInsensitiveEnum(CURRENCY_CODES),
  }),
  sku: z.string(),
  weight: z.object({
    unit: createEnum(WEIGHT_UNITS),
    value: z.number(),
  }),
}) satisfies z.ZodType<ItemDetailsForProcessing>;

export const DamagedItemResolutionSchema = z.object({
  exception_id: z.string(),
  item_details: ItemDetailsForProcessingSchema.optional(),
  resolution: createEnum(RESOLUTION_INSTRUCTIONS),
}) satisfies z.ZodType<DamagedItemResolution>;

export const ItemNotReceivedResolutionSchema = z.object({
  exception_id: z.string(),
  resolution: z.literal("ACKNOWLEDGE"),
}) satisfies z.ZodType<ItemNotReceivedResolution>;

export const UndeclaredItemResolutionSchema = z.object({
  exception_id: z.string(),
  item_details: ItemDetailsForProcessingSchema.optional(),
  resolution: createEnum(RESOLUTION_INSTRUCTIONS),
}) satisfies z.ZodType<UndeclaredItemResolution>;

export const ResolveReturnExceptionRequestSchema = z.array(
  z.union([
    DamagedItemResolutionSchema,
    ItemNotReceivedResolutionSchema,
    UndeclaredItemResolutionSchema,
  ]),
) satisfies z.ZodType<ResolveReturnExceptionRequest>;

export const TRACKING_EVENT_STATUSES = [
  "RECEIVED_AT_RETURN_POINT",
  "IN_TRANSIT_TO_HUB",
  "RECEIVED_AT_HUB",
  "APPROVED",
  "PENDING",
] as const;

export const ReturnCaseTrackingEventLocationSchema = z.object({
  city: z.string(),
  country_code: z.string().optional(),
  name: z.string(),
  postal_code: z.string().optional(),
  province: z.string().optional(),
}) satisfies z.ZodType<ReturnCaseTrackingEventLocation>;

export const ReturnCaseTrackingEventSchema = z.object({
  occurred_at: z.string().optional(),
  origin_location: ReturnCaseTrackingEventLocationSchema.optional(),
  status: createEnum(TRACKING_EVENT_STATUSES).optional(),
}) satisfies z.ZodType<ReturnCaseTrackingEvent>;
